import { TellescopeModelFromName, useUploadTellescopeFile } from "@tellescope/tanstack-query";
import { FormResponseValueAnswer } from "@tellescope/types-models";
import { Dispatch, SetStateAction, useCallback } from "react";
import type { LocalTellescopeFormResponseMap } from "./use-tellescope-form-core";
import type { FormShallowStoreHook } from "../../store";

export interface UsePendingFileUploadsArgs {
  fieldsMap: Record<string, TellescopeModelFromName<"form_fields">> | undefined;
  currentResponses: LocalTellescopeFormResponseMap;
  enduserId: string;
  setUnsavedResponses: Dispatch<SetStateAction<LocalTellescopeFormResponseMap>>;
  useFormShallowStore: FormShallowStoreHook;
}

export const usePendingFileUploads = ({
  fieldsMap,
  currentResponses,
  enduserId,
  setUnsavedResponses,
  useFormShallowStore,
}: UsePendingFileUploadsArgs) => {
  const {
    pendingFilesByFieldId: pendingFiles,
    setPendingFilesForField,
    clearPendingFilesForFields,
    uploadProgress,
    setUploadProgress,
  } = useFormShallowStore(
    "pendingFilesByFieldId",
    "setPendingFilesForField",
    "clearPendingFilesForFields",
    "uploadProgress",
    "setUploadProgress"
  );

  const { uploadFileAsync } = useUploadTellescopeFile();

  const uploadPendingFiles = useCallback(async (): Promise<LocalTellescopeFormResponseMap> => {
    const fieldIdsToUpload = Object.keys(pendingFiles).filter((fieldId) => {
      const field = fieldsMap?.[fieldId];
      if (!field || field.options?.autoUploadFiles) return false;
      return (pendingFiles[fieldId] ?? []).length > 0;
    });

    if (fieldIdsToUpload.length === 0) return currentResponses;

    const totalToUpload = fieldIdsToUpload.reduce((sum, fieldId) => sum + (pendingFiles[fieldId] ?? []).length, 0);
    setUploadProgress({ completed: 0, total: totalToUpload });
    let completedUploads = 0;

    const uploadedByFieldId = await Promise.all(
      fieldIdsToUpload.map(async (fieldId) => {
        const field = fieldsMap?.[fieldId];
        const isMultiple = field?.type === "files";
        const uploaded = await Promise.all(
          (pendingFiles[fieldId] ?? []).map(async (file) => {
            const record = await uploadFileAsync(
              {
                enduserId,
                name: file.name,
                size: file.size,
                type: file.type,
                hiddenFromEnduser: field?.options?.hideFromPortal,
              },
              file
            );
            completedUploads += 1;
            setUploadProgress({ completed: completedUploads, total: totalToUpload });
            return { name: record.name, secureName: record.secureName, type: isMultiple ? record.type : "file" };
          })
        );
        return { fieldId, uploaded, isMultiple };
      })
    );

    const nextResponses = uploadedByFieldId.reduce<LocalTellescopeFormResponseMap>(
      (acc, { fieldId, uploaded, isMultiple }) => {
        const existing = acc[fieldId];
        if (!existing) return acc;
        const prior = existing.answer;
        const answer: FormResponseValueAnswer = isMultiple
          ? { type: "files", value: [...(prior.type === "files" ? prior.value ?? [] : []), ...uploaded] }
          : { type: "file", value: uploaded[0] };
        return { ...acc, [fieldId]: { ...existing, answer } };
      },
      currentResponses
    );

    setUnsavedResponses((prev) =>
      fieldIdsToUpload.reduce((acc, fieldId) => {
        const next = nextResponses[fieldId];
        return next ? { ...acc, [fieldId]: next } : acc;
      }, prev)
    );
    clearPendingFilesForFields(fieldIdsToUpload);

    return nextResponses;
  }, [
    pendingFiles,
    currentResponses,
    setUploadProgress,
    setUnsavedResponses,
    clearPendingFilesForFields,
    fieldsMap,
    uploadFileAsync,
    enduserId,
  ]);

  return { pendingFiles, setPendingFilesForField, uploadPendingFiles, uploadProgress };
};
